import * as PropTypes from "prop-types";
import classNames from "classnames";
import Waypoint from "react-waypoint";
import { Motion, spring } from "react-motion";

import variablesStyles from "/styles/variables.css";
import flightLineAnimationStyles from "./FlightLineAnimation.css";
import { MOTION_PRESETS } from "/react/constants/motionPresets";
import { getAbsoluteTopOfElement } from "/javascripts/utils/getAbsoluteTopOfElement";

import { withAnimationFrame } from "/react/utils/withAnimationFrame";
import { withPageJump } from "/react/utils/withPageJump";
import { withViewport } from "/react/utils/withViewport";

export const defaultSvgProps = {
  height: "100%",
  preserveAspectRatio: "none",
  width: "100%",
  xmlns: "http://www.w3.org/2000/svg",
};

export const defaultPathProps = {
  stroke: variablesStyles.colorBlueDark,
  fill: "none",
  strokeWidth: 2,
};

@withAnimationFrame()
@withPageJump()
@withViewport()
export class FlightLineAnimation extends React.PureComponent {
  static propTypes = {
    children: PropTypes.node.isRequired,
    hasInDepthOverlay: PropTypes.bool,
    isLight: PropTypes.bool,
    lineAnimation: PropTypes.number.isRequired,
    noPaddingBottomMobile: PropTypes.bool,
    hasWhiteSpace: PropTypes.bool,
    hideOnMobile: PropTypes.bool,
  };

  containerRef = React.createRef();
  hasFullyAnimated = false;
  inViewportMargin = 250;
  pathRef = React.createRef();
  pathLength = 0;
  scrollStartingPoint = 0;
  scrollableDistance = 0;
  sectionAbsoluteTop = 0;
  springConfig = MOTION_PRESETS.FAST;

  state = {
    isAnimating: false,
    nextStrokeDashOffset: 0,
  };

  shouldAnimateToEnd = () => {
    return (
      !this.hasFullyAnimated &&
      this.props.pageIsJumping &&
      this.props.pageHasJumpedToPosition > this.sectionAbsoluteTop
    );
  };

  /*
  This calculates the exact height of the container outside of the viewport. This is the distance we need to scroll so
  the line animation is fully done.
   */
  setScrollableDistance = () => {
    const container = this.containerRef.current;
    const motionMargin = -50;

    if (!container) return;

    const containerDimensions = container.getBoundingClientRect();

    this.scrollStartingPoint = window.pageYOffset;
    this.scrollableDistance =
      containerDimensions.height -
      (this.props.viewportHeight - containerDimensions.top) -
      motionMargin;
  };

  setPathStyles = () => {
    const path = this.pathRef.current;

    if (!path) return;

    const pathLength = path.getTotalLength();

    path.style.strokeDasharray = `${pathLength}`;
    this.setState({ nextStrokeDashOffset: pathLength });
    this.pathLength = pathLength;
  };

  animateLine = () => {
    const scrolledDistance = window.pageYOffset - this.scrollStartingPoint;
    const scrolledPercentage = scrolledDistance / this.scrollableDistance;
    const nextStrokeDashOffset = (1 - scrolledPercentage) * this.pathLength;

    if (scrolledPercentage < 0) return;

    if (scrolledPercentage < 1) {
      this.setState({
        isAnimating: true,
        nextStrokeDashOffset,
      });
    } else {
      this.goToAnimationEnd();
    }
  };

  goToAnimationEnd = () => {
    this.setState({
      isAnimating: true,
      nextStrokeDashOffset: 0.1, // Safari flickers if set to zero.
    });
    this.hasFullyAnimated = true;
    this.props.stopRafLoop();
  };

  handleContainerInViewport = () => {
    if (
      this.hasFullyAnimated ||
      this.props.blockInViewportEvents ||
      !this.props.viewportIsLaptopOrBigger
    ) {
      return;
    }

    this.setScrollableDistance();
    this.props.startRafLoop(this.animateLine);
  };

  handleMountAndUpdate = () => {
    if (this.shouldAnimateToEnd()) {
      this.goToAnimationEnd();
    }
  };

  componentDidUpdate() {
    this.handleMountAndUpdate();
  }

  componentDidMount = () => {
    this.sectionAbsoluteTop = getAbsoluteTopOfElement(
      this.containerRef.current
    );
    this.setPathStyles();
    this.handleMountAndUpdate();
  };

  render() {
    const {
      children,
      hasInDepthOverlay,
      hasWhiteSpace,
      hideOnMobile,
      isLight,
      lineAnimation,
      noPaddingBottomMobile,
      viewportIsMobile,
      viewportIsLaptopOrBigger,
      viewportIsTabletOrSmaller,
    } = this.props;

    const { isAnimating, nextStrokeDashOffset } = this.state;

    const fill = isLight
      ? variablesStyles.colorWhite
      : variablesStyles.colorBlueDark;

    const desktopLineAnimationProps = {
      lineAnimation,
      nextStrokeDashOffset,
      pathProps: {
        ...defaultPathProps,
        stroke: fill,
        ref: this.pathRef,
      },
      springConfig: this.springConfig,
      svgProps: {
        ...defaultSvgProps,
        style: {
          opacity: isAnimating ? 1 : 0,
        },
      },
    };

    const mobileLineAnimationProps = {
      hasInDepthOverlay,
      viewportIsMobile,
    };

    return (
      <div
        className={flightLineAnimationStyles.container}
        ref={this.containerRef}
      >
        <Waypoint
          bottomOffset={this.inViewportMargin}
          onEnter={this.handleContainerInViewport}
        >
          <div className={flightLineAnimationStyles.lineAnimationContainer}>
            {viewportIsLaptopOrBigger ? (
              <DesktopLineAnimation {...desktopLineAnimationProps} />
            ) : !hideOnMobile ? (
              <MobileLineAnimation {...mobileLineAnimationProps} />
            ) : null}
          </div>
        </Waypoint>
        <div
          className={classNames(
            flightLineAnimationStyles.contentContainer,
            {
              [flightLineAnimationStyles.contentContainerHasInDepthOverlay]:
                hasInDepthOverlay,
            },
            { [flightLineAnimationStyles.whiteSpaced]: hasWhiteSpace },
            {
              [flightLineAnimationStyles.noPadding]:
                viewportIsTabletOrSmaller && hideOnMobile,
            },
            {
              [flightLineAnimationStyles.noPaddingBottom]:
                noPaddingBottomMobile,
            }
          )}
        >
          {children}
        </div>
      </div>
    );
  }
}

const DesktopLineAnimation = (props) => (
  <Motion
    style={{
      strokeDashOffset: spring(props.nextStrokeDashOffset, props.springConfig),
    }}
  >
    {(interpolatedStyle) => {
      props.pathProps.style = {
        strokeDashoffset: interpolatedStyle.strokeDashOffset,
      };

      return (
        <React.Fragment>
          {props.lineAnimation === 1 && (
            <svg {...props.svgProps} viewBox="0 0 1440 1563">
              <path
                {...props.pathProps}
                d="M1190 0c0 77.425-43.53 396.883-390.288 720.653C449.532 1047.62 252.662 1291.787 250 1563"
              />
            </svg>
          )}
          {props.lineAnimation === 2 && (
            <svg {...props.svgProps} viewBox="0 0 1440 955">
              <path
                {...props.pathProps}
                d="M250,0c-2,107.1,145.7,196.6,443.2,268.5c268.1,64.8,418.2,135.9,480.2,264.3c0.1,0.1,0.1,0.3,0.2,0.4
	c10.9,22.7,16.4,47.7,16.4,72.9V715v240"
              />
            </svg>
          )}
          {props.lineAnimation === 3 && (
            <svg {...props.svgProps} viewBox="0 0 1440 1466">
              <path
                {...props.pathProps}
                d="M1190.1,0v618.4c0,42.8-27.4,194.4-216.4,290.1c-323,181.9-723.6,208.8-723.6,412.2V1466"
              />
            </svg>
          )}
          {props.lineAnimation === 4 && (
            <svg {...props.svgProps} viewBox="0 0 1440 1842">
              <path
                {...props.pathProps}
                d="M1190 0c0 90.13-92.088 452.456-435.326 829.358-349 383.229-504.674 668.745-504.674 984.69V1842"
              />
            </svg>
          )}
          {props.lineAnimation === 5 && (
            <svg {...props.svgProps} viewBox="0 0 1440 400">
              <path {...props.pathProps} d="M252 0v400" />
            </svg>
          )}
          {props.lineAnimation === 6 && (
            <svg {...props.svgProps} viewBox="0 0 1440 698">
              <path {...props.pathProps} d="M1190 0v698" />
            </svg>
          )}
          {props.lineAnimation === 7 && (
            <svg {...props.svgProps} viewBox="0 0 1440 1600">
              <path
                {...props.pathProps}
                d="M1190-31c0,363.8-940,297.6-940,625.2v361.5V1612"
              />
            </svg>
          )}
          {props.lineAnimation === 8 && (
            <svg {...props.svgProps} viewBox="0 0 1440 882">
              <path {...props.pathProps} d="M720,0 L720,882" />
            </svg>
          )}
        </React.Fragment>
      );
    }}
  </Motion>
);

class MobileLineAnimation extends React.PureComponent {
  static propTypes = {
    hasInDepthOverlay: PropTypes.bool,
    viewportIsMobile: PropTypes.bool,
  };

  bottomLineHeight = parseInt(flightLineAnimationStyles.bottomLineHeight, 10);
  bottomLineHeightInDepthOverlay = parseInt(
    flightLineAnimationStyles.bottomLineHeightInDepthOverlay,
    10
  );
  bottomLineViewportMargin = this.bottomLineHeight * 2;
  lineHeightMobileDivider = flightLineAnimationStyles.lineHeightMobileDivider;
  springConfig = MOTION_PRESETS.FAST;
  topLineHeight = parseInt(flightLineAnimationStyles.topLineHeight, 10);
  topLineViewportMargin = this.topLineHeight * 6;

  state = {
    isBottomLineInViewport: false,
    isTopLineInViewport: false,
  };

  getBottomLineHeight = () => {
    if (this.props.hasInDepthOverlay) {
      return this.bottomLineHeightInDepthOverlay;
    }

    return this.props.viewportIsMobile
      ? this.bottomLineHeight * this.lineHeightMobileDivider
      : this.bottomLineHeight;
  };

  getTopLineHeight = () => {
    return this.props.viewportIsMobile
      ? this.topLineHeight * this.lineHeightMobileDivider
      : this.topLineHeight;
  };

  handleBottomLineInViewPort = () => {
    this.setState({
      isBottomLineInViewport: true,
    });
  };

  handleTopLineInViewPort = () => {
    this.setState({
      isTopLineInViewport: true,
    });
  };

  render() {
    const { hasInDepthOverlay } = this.props;

    const { isBottomLineInViewport, isTopLineInViewport } = this.state;

    const bottomLineHeight = this.getBottomLineHeight();
    const topLineHeight = this.getTopLineHeight();

    return (
      <Motion
        style={{
          topLineHeight: isTopLineInViewport
            ? spring(topLineHeight, this.springConfig)
            : 0,
          bottomLineHeight: isBottomLineInViewport
            ? spring(bottomLineHeight, this.springConfig)
            : 0,
        }}
      >
        {(interpolatedStyle) => (
          <React.Fragment>
            <div
              className={flightLineAnimationStyles.mobileFlightLineTop}
              style={{
                height: `${interpolatedStyle.topLineHeight}px`,
              }}
            >
              <Waypoint
                onEnter={this.handleTopLineInViewPort}
                bottomOffset={this.topLineViewportMargin}
              />
            </div>
            <div
              className={classNames(
                flightLineAnimationStyles.mobileFlightLineBottom,
                {
                  [flightLineAnimationStyles.mobileFlightLineBottomHasInDepthOverlay]:
                    hasInDepthOverlay,
                }
              )}
              style={{
                height: `${interpolatedStyle.bottomLineHeight}px`,
              }}
            >
              <Waypoint
                onEnter={this.handleBottomLineInViewPort}
                bottomOffset={this.bottomLineViewportMargin}
              />
            </div>
          </React.Fragment>
        )}
      </Motion>
    );
  }
}
